import { useRouter } from "next/router";

import { NoIndex } from "@/common/components/no-index";
import { buildListingPageFaqSchema } from "@/common/utils/json-ld.utils";
import { stripBrandSuffix } from "@/common/utils/seo-title.utils";
import { withUsHreflang } from "@/common/utils/us-launch.utils";
import { JsonLd } from "@/components/common/JsonLd";
import SEO from "@/components/common/seo";
import type { ListingPageContent } from "@/features/search/api/listing-page-content";
import type { ListingPageMeta } from "@/features/search/api/types";
import {
  type ListingHeadFacts,
  buildListingHeadDescription,
  buildListingHeadTitle,
  buildListingItemListSchema,
  isStubDescription,
} from "@/features/search/utils/listing-head.utils";
import { toAbsoluteUrl } from "@/features/search/utils/url.utils";

/**
 * Head tags for a curated landing page, from the service's `meta.page` plus Strapi's own SEO
 * fields where an editor filled them in.
 *
 * Strapi wins on title and description because those fields exist to be head tags — written to
 * length, as the old PLP used them. `meta.page.description` is the page's intro prose, which
 * runs past 700 characters on some brand pages; it stays the fallback, not the first choice.
 * URLs (canonical, hreflang) only ever come from the service — it owns the slugs.
 *
 * The `key` on each tag (canonical, og-url, the hreflang set) matches the app-wide
 * `<Canonical>` / `<HrefLang>` in `_app`, so these *replace* those rather than duplicating
 * them. Next's head de-duplication works on `key` alone: the 2026-09-03 head audit found
 * `og:url` twice on every listing page because it was the one tag without one (WHO-5505).
 * Renders nothing when there is no landing page behind the URL (a plain query search),
 * leaving the app-wide tags in place.
 *
 * WHO-5756: without Strapi copy the title and description are built from `headFacts` (the
 * live count and the brands on the page, see `utils/listing-head.utils.ts`). A service
 * description that is real prose still wins over the generated sentence; a stub does not.
 */
export const SearchSeo = ({
  page,
  content,
  headFacts = null,
}: {
  page: ListingPageMeta;
  content: ListingPageContent | null;
  headFacts?: ListingHeadFacts | null;
}) => {
  const { locale, locales = [], defaultLocale, query, asPath } = useRouter();

  if (!page) {
    return null;
  }

  /* Deeper result pages are the same page of content — say so, as the old PLP did. */
  const pageNumber = Number(query.page ?? 1);
  const withPageNumber = (text: string | null) =>
    text && pageNumber > 1 ? `${text} | P${pageNumber}` : text;
  /* Strip an editor-typed "| Whoppah" so the page number lands before the one suffix `SEO` adds. */
  const pageTitle = (text: string | null) =>
    text ? stripBrandSuffix(text) : text;

  /*
   * FAQ markup for the question-shaped headings in the page's own copy — the same passages a
   * reader sees in `PageDescription`, so the markup and the visible answers always ship
   * together. `null` when the copy asks no questions, which is most pages.
   *
   * Page 1 only: pages 2+ carry the identical copy, and repeating the markup across a paginated
   * set is what gets it ignored.
   */
  const faqSchema =
    pageNumber > 1
      ? null
      : buildListingPageFaqSchema(
          content?.description,
          asPath?.split("?")[0] ?? "",
          locale
        );

  /*
   * Pages 2+ are their own URL. Without the page number every deeper page
   * canonicalises to page 1 and the hreflang set stops being reciprocal, which
   * is what the 2026-09-03 head audit found on `/furniture?page=2`. Only `page`
   * is carried; the service URLs never hold other params.
   */
  const withPage = (href: string | undefined) => {
    if (!href || pageNumber <= 1) {
      return href;
    }

    const url = new URL(href);
    url.searchParams.set("page", String(pageNumber));

    return url.href;
  };

  const canonical = withPage(
    toAbsoluteUrl(page.canonicalUrl ?? page.url, locale, defaultLocale)
  );

  const generatedTitle =
    headFacts && page.title
      ? buildListingHeadTitle(stripBrandSuffix(page.title), headFacts, locale)
      : null;
  const generatedDescription =
    headFacts && page.title
      ? buildListingHeadDescription(
          stripBrandSuffix(page.title),
          headFacts,
          locale
        )
      : null;
  const serviceDescription = isStubDescription(page.description)
    ? generatedDescription ?? page.description
    : page.description;
  /*
   * Strapi carries the same stubs ("Tot 80% korting bij Whoppah" on `/nl/merken/bretz`): the
   * generated sentence replaces one when there is a sentence to replace it with.
   */
  const editorDescription =
    generatedDescription && isStubDescription(content?.seoDescription)
      ? null
      : content?.seoDescription ?? null;

  const itemListSchema =
    pageNumber > 1 || !headFacts || !canonical
      ? null
      : buildListingItemListSchema(
          headFacts.items.flatMap(item => {
            const url = toAbsoluteUrl(item.path, locale, defaultLocale);

            return url ? [{ name: item.name, url }] : [];
          }),
          canonical
        );

  const alternates = locales.flatMap(alternate => {
    const path =
      page.alternateUrls?.[alternate as keyof typeof page.alternateUrls];
    const href = withPage(toAbsoluteUrl(path, alternate, defaultLocale));

    return href ? [{ locale: alternate, href }] : [];
  });

  const defaultHref = alternates.find(
    alternate => alternate.locale === defaultLocale
  )?.href;

  return (
    <>
      {page.noIndex && <NoIndex />}
      {faqSchema && <JsonLd item={faqSchema} />}
      {itemListSchema && <JsonLd item={itemListSchema} />}
      <SEO
        title={
          withPageNumber(
            pageTitle(content?.seoTitle ?? generatedTitle ?? page.title)
          ) ?? undefined
        }
        description={
          withPageNumber(editorDescription ?? serviceDescription) ?? undefined
        }
        image={content?.seoImage ?? undefined}
      >
        {canonical && <link key="canonical" rel="canonical" href={canonical} />}
        {canonical && (
          <meta key="og-url" property="og:url" content={canonical} />
        )}
        {defaultHref && (
          <link
            key="default-hreflang"
            rel="alternate"
            hreflang="x-default"
            href={defaultHref}
          />
        )}
        {withUsHreflang(alternates).map(alternate => (
          <link
            key={`${alternate.locale}-hreflang`}
            rel="alternate"
            hreflang={alternate.locale}
            href={alternate.href}
          />
        ))}
      </SEO>
    </>
  );
};
